import React, { PropsWithChildren, useEffect, useState } from "react";
import { Button } from "reactstrap";
import { useDebounce } from "use-debounce";

import Loader from "../../common/Loader";
import NoDataMessage from "../../common/NoDataMessage";
import { PlatformPagedResult } from "../../common/platformTypes";
import MenuBar from "../MenuBar";

const PAGE_SIZE = 10;

export type PagedLoad<T> = (params: {
  search?: string;
  limit: number;
  offset: number;
}) => Promise<PlatformPagedResult<T>>;

type Props<T> = {
  title: string;
  placeholder: string;
  /** Singular noun for the empty state, e.g. "team". */
  componentName: string;
  /**
   * Referentially stable, or the query re-runs every render — wrap it in
   * `useCallback` keyed on whatever filters it closes over, which is also what
   * makes a filter change refetch.
   */
  load: PagedLoad<T>;
  /** Anything besides the search term that invalidates the current page. */
  filterKey?: string;
  renderTable: (items: T[]) => React.ReactNode;
};

/**
 * Search box, debounce, paging and the fetch lifecycle for the platform list
 * screens. Extra filter controls go in `children`, beside the search box; the
 * caller owns their state and folds them into `load` and `filterKey`.
 */
export function PagedListPage<T>({
  title,
  placeholder,
  componentName,
  load,
  filterKey,
  renderTable,
  children,
}: PropsWithChildren<Props<T>>) {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 500);
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // A new term or filter has to restart paging, or page 3 of "north" shows
  // nothing and reads as no matches. Derived rather than reset from an effect:
  // an effect leaves one render still holding the old page, and that render
  // fires a request at an offset the new query never asked for.
  const queryKey = JSON.stringify([debouncedSearch, filterKey ?? ""]);
  const [paged, setPaged] = useState({ queryKey, page: 0 });
  const page = paged.queryKey === queryKey ? paged.page : 0;
  const setPage = (next: number) => setPaged({ queryKey, page: next });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    load({
      search: debouncedSearch || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    })
      .then((result) => {
        if (!cancelled) {
          setItems(result.items);
          setTotal(result.total);
        }
      })
      .catch((e: Error) => {
        if (!cancelled) {
          setError(e.message);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [load, debouncedSearch, queryKey, page]);

  const hasMore = (page + 1) * PAGE_SIZE < total;

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col p-0">
          <MenuBar />
        </div>
      </div>
      <div className="row">
        <div className="col">
          <div className="card mt-2">
            <div className="card-header">
              <h2 className="card-title">{title}</h2>
              <div className="row">
                <div className="col">
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text">
                        <i className="fas fa-search" />
                      </span>
                    </div>{" "}
                    <input
                      className="form-control"
                      type="text"
                      placeholder={placeholder}
                      value={search}
                      onChange={(e) => setSearch(e.currentTarget.value)}
                    />
                  </div>
                </div>
                {children && <div className="col">{children}</div>}
              </div>
              <div className="my-2 text-muted">
                {loading ? "Loading..." : `${total} found`}
              </div>
            </div>
          </div>

          <div className="card bg-dark mt-2">
            {loading ? (
              <Loader />
            ) : error ? (
              <div className="alert alert-danger mb-0">{error}</div>
            ) : !items.length ? (
              <NoDataMessage componentName={componentName} />
            ) : (
              renderTable(items)
            )}
          </div>

          {!loading && !error && total > 0 && (
            <div className="d-flex justify-content-center align-items-center my-3">
              <Button
                size="sm"
                color="secondary"
                outline
                disabled={page === 0}
                onClick={() => setPage(Math.max(0, page - 1))}
              >
                <i className="fas fa-chevron-left mr-1" /> Previous
              </Button>
              <small className="text-muted mx-3">
                {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + items.length} of{" "}
                {total}
              </small>
              <Button
                size="sm"
                color="secondary"
                outline
                disabled={!hasMore}
                onClick={() => setPage(page + 1)}
              >
                Next <i className="fas fa-chevron-right ml-1" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
